import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { useModal } from '@shape-ds/components';
import { groupEvents } from 'pages/Asset/tabs/Events/utils/EventGrouping';
import { CaseForm } from 'queries/cases/types';
import { EventItem } from 'queries/events/types';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CollapseActionButton } from '../Collapse/CollapseTitle';
import { RelatedAlarmModalTable } from '../RelatedAlarmModalTable/RelatedAlarmModalTable';

interface RelatedAlarmResult {
  name: string;
  button: CollapseActionButton;
  groupedData: EventItem[];
  label: string;
  isDisabled: boolean;
  disabled: boolean;
  tooltip?: { title: string };
}

export const useRelatedAlarmHook = (): RelatedAlarmResult => {
  const { formState, setValue, getValues, watch } = useFormContext<CaseForm>();
  const { openModal } = useModal();
  const { t } = useTranslation();

  const caseId = getValues('id');
  const alarms = getValues('alarms') ?? [];
  const asset = watch('asset');

  const isDisabled = !asset?.id;

  const groupedData = useMemo(() => groupEvents(alarms), [alarms]);

  function handleClick<T extends React.MouseEvent<HTMLButtonElement>>(e: T): void {
    e.stopPropagation();

    if (asset) {
      openModal(
        <RelatedAlarmModalTable
          data={{ id: caseId, asset }}
          onSave={(newAlarms: EventItem[]) => {
            setValue('alarms', newAlarms, {
              shouldDirty: true,
            });
          }}
          isDisabled={formState.disabled}
        />,
        {
          title: t('case:related.table.title'),
          className: 'case-related-modal',
          width: '792px',
        },
      );
    }
  }

  const button = {
    icon: <AddCircleOutlineOutlined />,
    onClick: handleClick,
    text: t('case:related.addEdit'),
  };

  const name = 'relatedAlarms';

  return {
    name,
    label: t(`case:tabs.${name}`, { quantity: groupedData?.length }),
    button,
    tooltip: isDisabled ? { title: t('case:tooltip.asset_title_mandatory') } : undefined,
    groupedData,
    isDisabled,
    disabled: isDisabled,
  };
};
